import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Upload, X, Plus, Trash2, FileText, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const SUBGERENCIAS = [
  'SUBGERENCIA DE EMPRENDIMIENTO E INCLUSION PRODUCTIVA',
  'SUBGERENCIA DE GESTION',
  'SUBGERENCIA DE PRODUCTIVIDAD E INNOVACION',
  'SUBGERENCIA DE REINDUSTRIALIZACION Y ENCADENAMIENTOS PRODUCTIVOS',
  'SUBGERENCIA DE RELACIONAMIENTO',
  'GERENCIA GENERAL',
  'SUBGERENCIA DE REINDUSTRIALIZACION',
  'SUBGERENCIA DE COMPETITIVIDAD SOSTENIBLE Y TERRITORIAL',
  'SUBGERENCIA DE REINDUSTRIALIZACIÓN Y ENCADENAMIENTOS'
];

const TIPOS_BENEFICIARIO = [
  'MiPymes',
  'Emprendedores',
  'Unidades Productivas',
  'Empresas de Base Tecnológica',
  'Organizaciones Sociales',
  'Entidades Territoriales',
  'Persona Natural',
  'Persona Jurídica'
];

const TIPOS_CAMPO = [
  { value: 'texto', label: 'Texto corto' },
  { value: 'textarea', label: 'Texto largo' },
  { value: 'numero', label: 'Número' },
  { value: 'email', label: 'Email' },
  { value: 'telefono', label: 'Teléfono' },
  { value: 'fecha', label: 'Fecha' },
  { value: 'select', label: 'Lista desplegable' },
  { value: 'checkbox', label: 'Casilla de verificación' },
  { value: 'archivo', label: 'Archivo adjunto' }
];

export default function ConvocatoriaForm() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditing);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  
  const imageInputRef = useRef(null);
  const docInputRef = useRef(null);

  const [formData, setFormData] = useState({
    resolucion: '',
    contrato: '',
    codigo: '',
    nombre: '',
    descripcion: '',
    fecha_inicio: '',
    fecha_final: '',
    subgerencia: '',
    tipos_beneficiario: [],
    postular_una_vez: false,
    convocatoria_externa: false,
    enlace_externo: '',
    publicar_convocatoria: false,
    postulacion_presencial: false,
    hasta_agotar_recursos: false,
    terminos_referencia_url: '',
    terminos_referencia_archivos: [],
    imagen_url: '',
    estado_manual: 'activa',
    formulario_campos: [],
    tipo_convocatoria: 'publica',
    permite_empresa: true,
    permite_persona: true,
    requisitos_previos: []
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [resoluciones, setResoluciones] = useState([]);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchContratosResoluciones();
    if (isEditing) {
      fetchConvocatoria();
    }
  }, [id]);

  const fetchContratosResoluciones = async () => {
    try {
      const [contratosRes, resolucionesRes] = await Promise.all([
        fetch(`${API_URL}/api/contratos`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/resoluciones`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      
      if (contratosRes.ok) {
        const data = await contratosRes.json();
        setContratos(data);
      }
      if (resolucionesRes.ok) {
        const data = await resolucionesRes.json();
        setResoluciones(data);
      }
    } catch (error) {
      console.error('Error loading contratos/resoluciones:', error);
    }
  };

  const fetchConvocatoria = async () => {
    try {
      const response = await fetch(`${API_URL}/api/convocatorias/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Convocatoria no encontrada');

      const data = await response.json();
      
      // Convert date format from YYYY-MM-DD to input format
      setFormData({
        ...data,
        tipos_beneficiario: data.tipos_beneficiario || (data.tipo_beneficiario ? [data.tipo_beneficiario] : []),
        enlace_externo: data.enlace_externo || '',
        estado_manual: data.estado_manual || 'activa',
        formulario_campos: data.formulario_campos || [],
        requisitos_previos: data.requisitos_previos || []
      });
      
      if (data.imagen_url) {
        let imgUrl = data.imagen_url;
        if (imgUrl.startsWith('/uploads/')) imgUrl = '/api' + imgUrl;
        setImagePreview(imgUrl.startsWith('http') ? imgUrl : `${API_URL}${imgUrl}`);
      }
      // Load existing documents
      if (data.terminos_referencia_archivos && data.terminos_referencia_archivos.length > 0) {
        setUploadedDocs(data.terminos_referencia_archivos.map(doc => ({
          ...doc,
          url: doc.url.startsWith('/uploads/') ? '/api' + doc.url : doc.url
        })));
      } else if (data.terminos_referencia_url) {
        // Legacy support: convert single URL to array format
        let legacyUrl = data.terminos_referencia_url;
        if (legacyUrl.startsWith('/uploads/')) legacyUrl = '/api' + legacyUrl;
        setUploadedDocs([{
          url: legacyUrl,
          original_name: data.terminos_referencia_url.split('/').pop()
        }]);
      }
    } catch (error) {
      toast.error('Error al cargar la convocatoria');
      navigate('/convocatorias');
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleBeneficiarioToggle = (tipo) => {
    setFormData(prev => {
      const current = prev.tipos_beneficiario || [];
      if (current.includes(tipo)) {
        return { ...prev, tipos_beneficiario: current.filter(t => t !== tipo) };
      } else {
        return { ...prev, tipos_beneficiario: [...current, tipo] };
      }
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate image dimensions (should be 1:1)
    const img = new window.Image();
    img.onload = async () => {
      const aspectRatio = img.width / img.height;
      if (aspectRatio < 0.9 || aspectRatio > 1.1) {
        toast.error('La imagen debe tener formato 1:1 (cuadrada). Por favor seleccione otra imagen.');
        return;
      }

      // Upload image
      setUploadingImage(true);
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      try {
        const response = await fetch(`${API_URL}/api/upload/image`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formDataUpload
        });

        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.detail || 'Error al subir imagen');
        }

        const data = await response.json();
        setFormData(prev => ({ ...prev, imagen_url: data.url }));
        setImagePreview(URL.createObjectURL(file));
        toast.success('Imagen subida correctamente');
      } catch (error) {
        toast.error(error.message);
      } finally {
        setUploadingImage(false);
      }
    };
    img.src = URL.createObjectURL(file);
  };

  const handleDocUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // Validate total count
    if (uploadedDocs.length + files.length > 20) {
      toast.error('Máximo 20 archivos permitidos en total');
      return;
    }

    // Allowed MIME types
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/msword',
      'application/vnd.ms-excel'
    ];
    
    // Allowed extensions as fallback
    const allowedExtensions = ['.pdf', '.docx', '.xlsx', '.doc', '.xls'];

    // Validate each file
    for (const file of files) {
      if (file.size > 100 * 1024 * 1024) {
        toast.error(`"${file.name}" excede el límite de 100MB`);
        return;
      }
      
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      const isValidType = allowedTypes.includes(file.type) || allowedExtensions.includes(ext);
      
      if (!isValidType) {
        toast.error(`"${file.name}" no es un archivo permitido. Use PDF, DOCX o XLSX`);
        return;
      }
    }

    setUploadingDocs(true);
    
    try {
      // Upload files one by one to handle large files better
      const newDocs = [];
      for (const file of files) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        const response = await fetch(`${API_URL}/api/upload/document`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formDataUpload
        });

        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.detail || `Error al subir ${file.name}`);
        }

        const data = await response.json();
        newDocs.push({
          url: data.url,
          original_name: data.original_name || file.name,
          filename: data.filename
        });
      }

      setUploadedDocs(prev => [...prev, ...newDocs]);
      setFormData(prev => ({
        ...prev,
        terminos_referencia_archivos: [...(prev.terminos_referencia_archivos || []), ...newDocs]
      }));
      toast.success(`${newDocs.length} archivo(s) subido(s) correctamente`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploadingDocs(false);
      // Reset input
      if (docInputRef.current) docInputRef.current.value = '';
    }
  };

  const removeDocument = (index) => {
    setUploadedDocs(prev => prev.filter((_, i) => i !== index));
    setFormData(prev => ({
      ...prev,
      terminos_referencia_archivos: (prev.terminos_referencia_archivos || []).filter((_, i) => i !== index)
    }));
  };

  const addFormField = () => {
    setFormData(prev => ({
      ...prev,
      formulario_campos: [
        ...prev.formulario_campos,
        { id: Date.now(), nombre: '', tipo: 'texto', requerido: false, opciones: '' }
      ]
    }));
  };

  const updateFormField = (index, field, value) => {
    setFormData(prev => {
      const campos = [...prev.formulario_campos];
      campos[index] = { ...campos[index], [field]: value };
      return { ...prev, formulario_campos: campos };
    });
  };

  const removeFormField = (index) => {
    setFormData(prev => ({
      ...prev,
      formulario_campos: prev.formulario_campos.filter((_, i) => i !== index)
    }));
  };

  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  const formatDateForInput = (displayDate) => {
    if (!displayDate) return '';
    if (displayDate.includes('-')) return displayDate; // Already in YYYY-MM-DD
    const [day, month, year] = displayDate.split('/');
    return `${year}-${month}-${day}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validación de campos obligatorios con mensajes específicos
    const camposObligatorios = [
      { campo: 'resolucion', nombre: 'Resolución', descripcion: 'Seleccione la resolución que ampara esta convocatoria' },
      { campo: 'contrato', nombre: 'Contrato', descripcion: 'Seleccione el contrato asociado a esta convocatoria' },
      { campo: 'nombre', nombre: 'Nombre', descripcion: 'Ingrese el nombre de la convocatoria' },
      { campo: 'descripcion', nombre: 'Descripción', descripcion: 'Ingrese una descripción detallada de la convocatoria' },
      { campo: 'fecha_inicio', nombre: 'Fecha de Inicio', descripcion: 'Seleccione la fecha en que inicia la convocatoria' },
      { campo: 'fecha_final', nombre: 'Fecha Final', descripcion: 'Seleccione la fecha en que cierra la convocatoria' },
    ];

    const camposFaltantes = camposObligatorios.filter(c => !formData[c.campo]);
    
    if (camposFaltantes.length > 0) {
      const primerCampoFaltante = camposFaltantes[0];
      toast.error(`Campo obligatorio pendiente: ${primerCampoFaltante.nombre}`, {
        description: primerCampoFaltante.descripcion,
        duration: 5000
      });
      return;
    }

    if (formData.tipos_beneficiario.length === 0) {
      toast.error('Campo obligatorio pendiente: Tipos de Beneficiario', {
        description: 'Seleccione al menos un tipo de beneficiario para esta convocatoria',
        duration: 5000
      });
      return;
    }

    if (new Date(formData.fecha_final) < new Date(formData.fecha_inicio)) {
      toast.error('Error en las fechas', {
        description: 'La fecha final debe ser posterior a la fecha de inicio',
        duration: 5000
      });
      return;
    }

    if (formData.convocatoria_externa && !formData.enlace_externo) {
      toast.error('Campo obligatorio pendiente: Enlace del Formulario Externo', {
        description: 'Al marcar "Convocatoria Externa" debe proporcionar el enlace del formulario',
        duration: 5000
      });
      return;
    }

    try {
      setLoading(true);
      const url = isEditing 
        ? `${API_URL}/api/convocatorias/${id}`
        : `${API_URL}/api/convocatorias`;
      
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al guardar la convocatoria');
      }

      toast.success(isEditing ? 'Convocatoria actualizada' : 'Convocatoria creada');
      navigate('/convocatorias');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const RadioOption = ({ name, value, checked, onChange, label }) => (
    <button
      type="button"
      onClick={() => onChange({ target: { name, value, type: 'checkbox', checked: value } })}
      className={`px-4 py-2 text-sm font-medium border transition-colors ${
        checked === value
          ? 'bg-[#0F172A] text-white border-[#0F172A]'
          : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
      } ${label === 'No' ? 'rounded-l-lg' : 'rounded-r-lg'}`}
    >
      {label}
    </button>
  );

  if (loadingData) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-lg text-slate-600">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-[#0F172A] border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_login-verify-system/artifacts/gyieqwft_Captura%20de%20Pantalla%202025-12-10%20a%20la%28s%29%207.03.41%20a.%C2%A0m..png" 
              alt="Logo" 
              className="h-12 w-auto object-contain brightness-0 invert"
            />
            <div className="border-l border-slate-600 pl-4">
              <h1 className="text-xl font-semibold text-white">
                {isEditing ? 'Editar Convocatoria' : 'Nueva Convocatoria'}
              </h1>
            </div>
          </div>
          <button
            onClick={() => navigate('/convocatorias')}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Volver</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 md:p-8">
          <h2 className="text-xl font-bold text-slate-900 mb-6">
            CONVOCATORIA / INSTRUMENTO / PROGRAMA
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6" noValidate>
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Resolución <span className="text-red-500">*</span>
                </label>
                <select name="resolucion" value={formData.resolucion} onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${!formData.resolucion ? 'border-slate-300' : 'border-green-300 bg-green-50'}`}>
                  <option value="">Seleccione...</option>
                  {resoluciones.map(res => <option key={res.id} value={res.numero}>{res.numero} - {res.nombre}</option>)}
                </select>
                <p className="text-xs text-slate-500 mt-1">Resolución que ampara esta convocatoria</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Contrato <span className="text-red-500">*</span>
                </label>
                <select name="contrato" value={formData.contrato} onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${!formData.contrato ? 'border-slate-300' : 'border-green-300 bg-green-50'}`}>
                  <option value="">Seleccione...</option>
                  {contratos.map(cont => <option key={cont.id} value={cont.numero}>{cont.numero} - {cont.nombre}</option>)}
                </select>
                <p className="text-xs text-slate-500 mt-1">Contrato asociado a esta convocatoria</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Código
                </label>
                <input type="text" name="codigo" value={isEditing ? formData.codigo : '(Generado automáticamente)'} 
                  readOnly disabled
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-500 cursor-not-allowed" />
                <p className="text-xs text-slate-500 mt-1">El código se genera automáticamente al crear la convocatoria</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Nombre <span className="text-red-500">*</span>
                </label>
                <input type="text" name="nombre" value={formData.nombre} onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${!formData.nombre ? 'border-slate-300' : 'border-green-300 bg-green-50'}`} />
                <p className="text-xs text-slate-500 mt-1">Nombre de la convocatoria</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Descripción <span className="text-red-500">*</span>
              </label>
              <textarea name="descripcion" value={formData.descripcion} onChange={handleChange} rows={4}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${!formData.descripcion ? 'border-slate-300' : 'border-green-300 bg-green-50'}`} />
              <p className="text-xs text-slate-500 mt-1">Descripción detallada de la convocatoria</p>
            </div>

            {/* Dates - DD/MM/YYYY format */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Fecha Inicio (DD/MM/YYYY) <span className="text-red-500">*</span>
                </label>
                <input type="date" name="fecha_inicio" value={formData.fecha_inicio} onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${!formData.fecha_inicio ? 'border-slate-300' : 'border-green-300 bg-green-50'}`} />
                {formData.fecha_inicio && (
                  <p className="text-xs text-green-600 mt-1">
                    Fecha seleccionada: {formatDateForDisplay(formData.fecha_inicio)}
                  </p>
                )}
                {!formData.fecha_inicio && (
                  <p className="text-xs text-slate-500 mt-1">Fecha en que inicia la convocatoria</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Fecha Final (DD/MM/YYYY) <span className="text-red-500">*</span>
                </label>
                <input type="date" name="fecha_final" value={formData.fecha_final} onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${!formData.fecha_final ? 'border-slate-300' : 'border-green-300 bg-green-50'}`} />
                {formData.fecha_final && (
                  <p className="text-xs text-green-600 mt-1">
                    Fecha seleccionada: {formatDateForDisplay(formData.fecha_final)}
                  </p>
                )}
                {!formData.fecha_final && (
                  <p className="text-xs text-slate-500 mt-1">Fecha en que cierra la convocatoria</p>
                )}
              </div>
            </div>

            {/* Subgerencia */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Subgerencia</label>
              <select name="subgerencia" value={formData.subgerencia} onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                <option value="">Seleccione...</option>
                {SUBGERENCIAS.map(sub => <option key={sub} value={sub}>{sub}</option>)}
              </select>
            </div>

            {/* Multi-select Beneficiarios */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Tipo de Beneficiario Final <span className="text-red-500">*</span>
                <span className="text-xs text-slate-500 ml-2">(Seleccione uno o más)</span>
              </label>
              {formData.tipos_beneficiario.length === 0 && (
                <p className="text-xs text-orange-600 mb-2">⚠️ Debe seleccionar al menos un tipo de beneficiario</p>
              )}
              {formData.tipos_beneficiario.length > 0 && (
                <p className="text-xs text-green-600 mb-2">✓ {formData.tipos_beneficiario.length} tipo(s) seleccionado(s)</p>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {TIPOS_BENEFICIARIO.map(tipo => (
                  <label key={tipo} className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                    formData.tipos_beneficiario?.includes(tipo) 
                      ? 'bg-blue-50 border-blue-500 text-blue-700' 
                      : 'bg-white border-slate-300 hover:bg-slate-50'
                  }`}>
                    <input
                      type="checkbox"
                      checked={formData.tipos_beneficiario?.includes(tipo) || false}
                      onChange={() => handleBeneficiarioToggle(tipo)}
                      className="h-4 w-4 text-blue-600 rounded"
                    />
                    <span className="text-sm">{tipo}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Estado Manual */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Estado de la Convocatoria
              </label>
              <div className="flex gap-2">
                {['activa', 'pausada', 'cerrada'].map(estado => (
                  <button
                    key={estado}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, estado_manual: estado }))}
                    className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                      formData.estado_manual === estado
                        ? estado === 'activa' ? 'bg-green-600 text-white border-green-600'
                        : estado === 'pausada' ? 'bg-yellow-500 text-white border-yellow-500'
                        : 'bg-red-600 text-white border-red-600'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    {estado.charAt(0).toUpperCase() + estado.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Tipo de Convocatoria */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Tipo de Convocatoria</label>
                <div className="flex flex-wrap gap-4">
                  <label className={`flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer ${
                    formData.tipo_convocatoria === 'publica' ? 'bg-green-50 border-green-500' : 'bg-white border-slate-300'
                  }`}>
                    <input 
                      type="radio" 
                      name="tipo_convocatoria" 
                      value="publica"
                      checked={formData.tipo_convocatoria === 'publica'}
                      onChange={(e) => setFormData(prev => ({ ...prev, tipo_convocatoria: e.target.value }))}
                      className="text-green-600"
                      data-testid="tipo-convocatoria-publica"
                    />
                    <span className={formData.tipo_convocatoria === 'publica' ? 'text-green-700 font-medium' : 'text-slate-700'}>Pública</span>
                  </label>
                  <label className={`flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer ${
                    formData.tipo_convocatoria === 'privada' ? 'bg-purple-50 border-purple-500' : 'bg-white border-slate-300'
                  }`}>
                    <input 
                      type="radio" 
                      name="tipo_convocatoria" 
                      value="privada"
                      checked={formData.tipo_convocatoria === 'privada'}
                      onChange={(e) => setFormData(prev => ({ ...prev, tipo_convocatoria: e.target.value }))}
                      className="text-purple-600"
                      data-testid="tipo-convocatoria-privada"
                    />
                    <span className={formData.tipo_convocatoria === 'privada' ? 'text-purple-700 font-medium' : 'text-slate-700'}>Privada</span>
                  </label>
                  <label className={`flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer ${
                    formData.tipo_convocatoria === 'especial' ? 'bg-amber-50 border-amber-500' : 'bg-white border-slate-300'
                  }`}>
                    <input 
                      type="radio" 
                      name="tipo_convocatoria" 
                      value="especial"
                      checked={formData.tipo_convocatoria === 'especial'}
                      onChange={(e) => setFormData(prev => ({ ...prev, tipo_convocatoria: e.target.value }))}
                      className="text-amber-600"
                      data-testid="tipo-convocatoria-especial"
                    />
                    <span className={formData.tipo_convocatoria === 'especial' ? 'text-amber-700 font-medium' : 'text-slate-700'}>Especial</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Permite postulación de</label>
                <div className="flex gap-4">
                  <label className={`flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer ${
                    formData.permite_persona ? 'bg-blue-50 border-blue-500' : 'bg-white border-slate-300'
                  }`}>
                    <input 
                      type="checkbox" 
                      checked={formData.permite_persona}
                      onChange={(e) => setFormData(prev => ({ ...prev, permite_persona: e.target.checked }))}
                      className="text-blue-600 rounded"
                    />
                    <span className={formData.permite_persona ? 'text-blue-700 font-medium' : 'text-slate-700'}>Persona</span>
                  </label>
                  <label className={`flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer ${
                    formData.permite_empresa ? 'bg-orange-50 border-orange-500' : 'bg-white border-slate-300'
                  }`}>
                    <input 
                      type="checkbox" 
                      checked={formData.permite_empresa}
                      onChange={(e) => setFormData(prev => ({ ...prev, permite_empresa: e.target.checked }))}
                      className="text-orange-600 rounded"
                    />
                    <span className={formData.permite_empresa ? 'text-orange-700 font-medium' : 'text-slate-700'}>Empresa</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Requisitos Previos (solo para tipo especial) */}
            {formData.tipo_convocatoria === 'especial' && (
              <div className="border-l-4 border-amber-500 bg-amber-50 rounded-lg p-4" data-testid="requisitos-section">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-semibold text-amber-900 flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Requisitos previos (checklist antes de postular)
                    </h4>
                    <p className="text-xs text-amber-800 mt-1">
                      El postulante deberá confirmar cada requisito antes de acceder al formulario.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      requisitos_previos: [...(prev.requisitos_previos || []), '']
                    }))}
                    className="flex items-center gap-1 px-3 py-1.5 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700"
                    data-testid="add-requisito-btn"
                  >
                    <Plus className="h-4 w-4" /> Agregar requisito
                  </button>
                </div>
                <div className="space-y-2">
                  {(formData.requisitos_previos || []).length === 0 && (
                    <p className="text-sm text-amber-700 italic">Aún no has agregado requisitos.</p>
                  )}
                  {(formData.requisitos_previos || []).map((req, idx) => (
                    <div key={idx} className="flex items-start gap-2" data-testid={`requisito-row-${idx}`}>
                      <span className="mt-2 text-sm font-medium text-amber-800 w-6">{idx + 1}.</span>
                      <textarea
                        rows={2}
                        value={req}
                        onChange={(e) => {
                          const arr = [...(formData.requisitos_previos || [])];
                          arr[idx] = e.target.value;
                          setFormData(prev => ({ ...prev, requisitos_previos: arr }));
                        }}
                        placeholder="Ej: Ser una persona jurídica colombiana con al menos 2 años de constitución."
                        className="flex-1 px-3 py-2 border border-amber-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 bg-white"
                        data-testid={`requisito-input-${idx}`}
                      />
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          requisitos_previos: (prev.requisitos_previos || []).filter((_, i) => i !== idx)
                        }))}
                        className="mt-2 p-1.5 text-red-600 hover:bg-red-50 rounded"
                        data-testid={`remove-requisito-${idx}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Boolean Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Postular una vez *</label>
                <div className="flex">
                  <RadioOption name="postular_una_vez" value={false} checked={formData.postular_una_vez} onChange={handleChange} label="No" />
                  <RadioOption name="postular_una_vez" value={true} checked={formData.postular_una_vez} onChange={handleChange} label="Si" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">¿Publicar convocatoria? *</label>
                <div className="flex">
                  <RadioOption name="publicar_convocatoria" value={false} checked={formData.publicar_convocatoria} onChange={handleChange} label="No" />
                  <RadioOption name="publicar_convocatoria" value={true} checked={formData.publicar_convocatoria} onChange={handleChange} label="Si" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">¿Postulación presencial? *</label>
                <div className="flex">
                  <RadioOption name="postulacion_presencial" value={false} checked={formData.postulacion_presencial} onChange={handleChange} label="No" />
                  <RadioOption name="postulacion_presencial" value={true} checked={formData.postulacion_presencial} onChange={handleChange} label="Si" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">¿Hasta agotar recursos? *</label>
                <div className="flex">
                  <RadioOption name="hasta_agotar_recursos" value={false} checked={formData.hasta_agotar_recursos} onChange={handleChange} label="No" />
                  <RadioOption name="hasta_agotar_recursos" value={true} checked={formData.hasta_agotar_recursos} onChange={handleChange} label="Si" />
                </div>
              </div>
            </div>

            {/* Convocatoria Externa */}
            <div className="border-t border-slate-200 pt-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Convocatoria Externa *</label>
                <div className="flex">
                  <RadioOption name="convocatoria_externa" value={false} checked={formData.convocatoria_externa} onChange={handleChange} label="No" />
                  <RadioOption name="convocatoria_externa" value={true} checked={formData.convocatoria_externa} onChange={handleChange} label="Si" />
                </div>
              </div>
              
              {formData.convocatoria_externa && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Enlace del Formulario Externo <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="url"
                    name="enlace_externo"
                    value={formData.enlace_externo}
                    onChange={handleChange}
                    placeholder="https://forms.google.com/..."
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
            </div>

            {/* File Uploads */}
            <div className="border-t border-slate-200 pt-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Archivos Adjuntos</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Imagen (formato 1:1, máx 10MB)
                  </label>
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  
                  {imagePreview ? (
                    <div className="relative">
                      <img src={imagePreview} alt="Preview" className="w-full h-48 object-cover rounded-lg border" />
                      <button
                        type="button"
                        onClick={() => { setImagePreview(null); setFormData(prev => ({ ...prev, imagen_url: '' })); }}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => imageInputRef.current?.click()}
                      disabled={uploadingImage}
                      className="w-full h-48 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-blue-500 hover:bg-blue-50 transition-colors"
                    >
                      {uploadingImage ? (
                        <span className="text-slate-500">Subiendo...</span>
                      ) : (
                        <>
                          <ImageIcon className="h-8 w-8 text-slate-400" />
                          <span className="text-sm text-slate-500">Haga clic para subir imagen</span>
                          <span className="text-xs text-slate-400">JPG, PNG, GIF, WEBP (1:1, máx 10MB)</span>
                        </>
                      )}
                    </button>
                  )}
                </div>

                {/* PDF/DOCX/XLSX Upload - Multiple Files */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Términos de Referencia (hasta 20 archivos, máx 100MB c/u)
                  </label>
                  <input
                    ref={docInputRef}
                    type="file"
                    accept=".pdf,.docx,.xlsx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    multiple
                    onChange={handleDocUpload}
                    className="hidden"
                  />
                  
                  {/* List of uploaded documents */}
                  {uploadedDocs.length > 0 && (
                    <div className="mb-3 space-y-2 max-h-36 overflow-y-auto">
                      {uploadedDocs.map((doc, index) => (
                        <div key={index} className="p-2 border border-slate-200 rounded-lg bg-slate-50 flex items-center justify-between">
                          <div className="flex items-center gap-2 overflow-hidden">
                            <FileText className="h-5 w-5 text-red-500 flex-shrink-0" />
                            <div className="overflow-hidden">
                              <p className="text-xs font-medium text-slate-700 truncate">{doc.original_name}</p>
                              <a 
                                href={`${API_URL}${doc.url}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:underline"
                              >
                                Ver
                              </a>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeDocument(index)}
                            className="p-1 text-slate-400 hover:text-red-500 flex-shrink-0"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {uploadedDocs.length < 20 && (
                    <button
                      type="button"
                      onClick={() => docInputRef.current?.click()}
                      disabled={uploadingDocs}
                      className={`w-full ${uploadedDocs.length > 0 ? 'h-20' : 'h-48'} border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-blue-500 hover:bg-blue-50 transition-colors`}
                    >
                      {uploadingDocs ? (
                        <span className="text-slate-500">Subiendo archivos...</span>
                      ) : (
                        <>
                          <FileText className="h-6 w-6 text-slate-400" />
                          <span className="text-sm text-slate-500">
                            {uploadedDocs.length > 0 ? 'Agregar más archivos' : 'Haga clic para subir archivos'}
                          </span>
                          <span className="text-xs text-slate-400">
                            {uploadedDocs.length}/20 archivos · PDF, DOCX, XLSX · máx 100MB c/u
                          </span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Formulario de Postulación (Solo para convocatorias internas) */}
            {!formData.convocatoria_externa && (
              <div className="border-t border-slate-200 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-slate-900">
                    Formulario de Postulación
                    <span className="text-sm font-normal text-slate-500 ml-2">(Campos personalizados)</span>
                  </h3>
                  <button
                    type="button"
                    onClick={addFormField}
                    className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                  >
                    <Plus className="h-4 w-4" />
                    Agregar Campo
                  </button>
                </div>

                {formData.formulario_campos?.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-8 border border-dashed border-slate-300 rounded-lg">
                    No hay campos personalizados. Haga clic en "Agregar Campo" para crear el formulario de postulación.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {formData.formulario_campos?.map((campo, index) => (
                      <div key={campo.id} className="p-4 border border-slate-200 rounded-lg bg-slate-50">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-slate-600 mb-1">Nombre del Campo</label>
                              <input
                                type="text"
                                value={campo.nombre}
                                onChange={(e) => updateFormField(index, 'nombre', e.target.value)}
                                placeholder="Ej: Número de empleados"
                                className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-600 mb-1">Tipo de Campo</label>
                              <select
                                value={campo.tipo}
                                onChange={(e) => updateFormField(index, 'tipo', e.target.value)}
                                className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded"
                              >
                                {TIPOS_CAMPO.map(t => (
                                  <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-600 mb-1">¿Requerido?</label>
                              <select
                                value={campo.requerido ? 'si' : 'no'}
                                onChange={(e) => updateFormField(index, 'requerido', e.target.value === 'si')}
                                className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded"
                              >
                                <option value="no">No</option>
                                <option value="si">Sí</option>
                              </select>
                            </div>
                            {campo.tipo === 'select' && (
                              <div>
                                <label className="block text-xs font-medium text-slate-600 mb-1">Opciones (separadas por coma)</label>
                                <input
                                  type="text"
                                  value={campo.opciones}
                                  onChange={(e) => updateFormField(index, 'opciones', e.target.value)}
                                  placeholder="Opción 1, Opción 2, Opción 3"
                                  className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded"
                                />
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFormField(index)}
                            className="p-1.5 text-slate-400 hover:text-red-500"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-slate-200">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 sm:flex-none px-8 py-3 bg-[#0F172A] text-white font-semibold rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-50"
              >
                {loading ? 'GUARDANDO...' : isEditing ? 'GUARDAR CAMBIOS' : 'REGISTRAR'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/convocatorias')}
                className="flex-1 sm:flex-none px-8 py-3 bg-slate-600 text-white font-semibold rounded-lg hover:bg-slate-700 transition-colors"
              >
                VOLVER
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
